import type { NatalDecorativeCycle, NormalizedChart, PalaceId } from './domain.js';
import type { GeneratedInterpretation } from './interpretation.js';
import { topicInterpretationViewModel, type ClaimCardViewModel } from './presentation.js';
import type { ChronologyResolution } from './chronology.js';
import { buildCalculationContextViewModel, type CalculationContextViewModel } from './calculationContextPresentation.js';
import { IZTRO_DEFAULT_108_STAR_CATALOG } from './starCatalog.js';
import { buildOverviewDigestFromTopics, type OverviewDigestViewModel } from './overviewDigest.js';
import { extractNatalFeatures } from './featureExtractor.js';
import { buildClaimExplanationPacket } from './explainability.js';
import { buildTopicSyntheses, type TopicSynthesisViewModel } from './topicSynthesis.js';
import { buildFullChartSynthesis, type FullChartSynthesisViewModel } from './fullChartSynthesis.js';
import { buildTopicStructuralContexts, type TopicStructuralContextViewModel } from './topicStructuralContext.js';
import { buildOverviewReaderDepth, type OverviewReaderDepthViewModel } from './overviewReaderDepth.js';

export type PrintBirthPrivacy = 'full' | 'redacted';
export type PrintDetailLevel = 'summary' | 'evidence';

export interface PrintBirthSummary {
  privacy: PrintBirthPrivacy;
  solarDate?: string;
  lunarDate?: string;
  timeLabel?: string;
  gender?: 'male' | 'female';
  zodiac: string;
  fiveElementsClass: string;
}

export interface PrintPalaceSummary {
  id: PalaceId;
  label: string;
  heavenlyStem: string;
  earthlyBranch: string;
  isBodyPalace: boolean;
  majorStars: readonly string[];
  minorStars: readonly string[];
  adjectiveStars: readonly string[];
  decorativeStars: readonly { label: string; cycle: NatalDecorativeCycle; }[];
}

export interface PrintStarCoverage {
  catalog: string;
  corePlacements: number;
  decorativePlacements: number;
  totalPlacements: number;
  uniqueStarLabels: number;
  complete108: boolean;
}

export interface PrintInterpretationSection {
  topic: GeneratedInterpretation['topic'];
  claims: readonly ClaimCardViewModel[];
}

export interface PrintPacket {
  schemaVersion: '1';
  birth: PrintBirthSummary;
  ruleset: string;
  interpretationProvenance?: Readonly<{ school:string; sourceRegistry:string; rulePack:string; templatePack:string }>;
  calculationContext?: CalculationContextViewModel;
  palaces: readonly PrintPalaceSummary[];
  starCoverage: PrintStarCoverage;
  overviewDigest: OverviewDigestViewModel;
  overviewReaderDepth: OverviewReaderDepthViewModel;
  interpretations: readonly PrintInterpretationSection[];
  topicSyntheses: readonly TopicSynthesisViewModel[];
  topicStructuralContexts: readonly TopicStructuralContextViewModel[];
  fullChartSynthesis: FullChartSynthesisViewModel;
  detailLevel: PrintDetailLevel;
  evidenceAppendix?: readonly { topic: GeneratedInterpretation['topic']; claimKey: string; ruleIds: readonly string[]; sourceLocators: readonly string[]; evidenceRefs: readonly string[]; }[];
  disclosure: string;
}

export function buildPrintPacket(
  chart: NormalizedChart,
  interpretations: readonly GeneratedInterpretation[],
  privacy: PrintBirthPrivacy = 'redacted',
  detailLevel: PrintDetailLevel = 'summary',
  chronology?: Readonly<{ birth: ChronologyResolution; target?: ChronologyResolution }>,
): PrintPacket {
  const birth: PrintBirthSummary = privacy === 'full'
    ? {
        privacy,
        solarDate: chart.birth.solarDate,
        lunarDate: chart.birth.lunarDate,
        timeLabel: chart.birth.timeLabel,
        gender: chart.birth.gender,
        zodiac: chart.zodiac,
        fiveElementsClass: chart.fiveElementsClass,
      }
    : {
        privacy,
        zodiac: chart.zodiac,
        fiveElementsClass: chart.fiveElementsClass,
      };

  const allPalaces = chart.palaceOrder.map((id) => chart.palaces[id]);
  const corePlacements = allPalaces.reduce((sum, palace) => sum + palace.majorStars.length + palace.minorStars.length + palace.adjectiveStars.length, 0);
  const decorativePlacements = allPalaces.reduce((sum, palace) => sum + (palace.decorativeStars ?? []).length, 0);
  const labels = allPalaces.flatMap((palace) => [...palace.majorStars, ...palace.minorStars, ...palace.adjectiveStars].map((star) => star.label).concat((palace.decorativeStars ?? []).map((star) => star.label)));
  const totalPlacements = corePlacements + decorativePlacements;
  const uniqueStarLabels = new Set(labels).size;
  const overviewDigest = buildOverviewDigestFromTopics(interpretations);
  const versionRows=[...new Map(interpretations.map((item)=>[`${item.versions.interpretationRulePack}|${item.versions.templatePack}|${item.versions.interpretationSchool??''}|${item.versions.sourceRegistry}`,item.versions] as const)).values()];
  if(versionRows.length>1) throw new Error('Print packet rejected mixed interpretation version provenance');
  const versionRow=versionRows[0];
  const detailedInterpretations = interpretations.filter((result) => result.topic !== 'overview');
  const topicViews = Object.freeze(detailedInterpretations.map(topicInterpretationViewModel));
  const features = extractNatalFeatures(chart);
  const explanations = Object.freeze(detailedInterpretations.flatMap((result) => result.claims.map((claim) => buildClaimExplanationPacket(claim, features))));
  const topicSyntheses = buildTopicSyntheses(topicViews, explanations);
  const fullChartSynthesis = buildFullChartSynthesis(topicViews, topicSyntheses);
  const overviewReaderDepth = buildOverviewReaderDepth(overviewDigest, topicSyntheses, fullChartSynthesis);
  const topicStructuralContexts = buildTopicStructuralContexts(chart, topicViews.map((item)=>item.topic));
  const starCoverage: PrintStarCoverage = Object.freeze({
    catalog: `${IZTRO_DEFAULT_108_STAR_CATALOG.id}@${IZTRO_DEFAULT_108_STAR_CATALOG.version}`,
    corePlacements, decorativePlacements, totalPlacements, uniqueStarLabels,
    complete108: totalPlacements === IZTRO_DEFAULT_108_STAR_CATALOG.placementCount && uniqueStarLabels === IZTRO_DEFAULT_108_STAR_CATALOG.uniqueStarCount,
  });

  return {
    schemaVersion: '1',
    birth,
    ruleset: `${chart.calculation.rulesetId}@${chart.calculation.rulesetVersion}`,
    ...(versionRow?.interpretationSchool?{interpretationProvenance:Object.freeze({school:versionRow.interpretationSchool,sourceRegistry:versionRow.sourceRegistry,rulePack:versionRow.interpretationRulePack,templatePack:versionRow.templatePack})}:{}),
    ...(chronology ? { calculationContext: buildCalculationContextViewModel(chart, chronology) } : {}),
    starCoverage,
    overviewDigest,
    overviewReaderDepth,
    palaces: chart.palaceOrder.map((id) => {
      const palace = chart.palaces[id];
      return {
        id,
        label: palace.label,
        heavenlyStem: palace.heavenlyStem,
        earthlyBranch: palace.earthlyBranch,
        isBodyPalace: palace.isBodyPalace,
        majorStars: palace.majorStars.map((star) => star.label),
        minorStars: palace.minorStars.map((star) => star.label),
        adjectiveStars: palace.adjectiveStars.map((star) => star.label),
        decorativeStars: (palace.decorativeStars ?? []).map((star) => ({ label: star.label, cycle: star.cycle })),
      };
    }),
    interpretations: topicViews.map((view) => ({ topic: view.topic, claims: view.claims })),
    topicSyntheses,
    topicStructuralContexts,
    fullChartSynthesis,
    detailLevel,
    ...(detailLevel === 'evidence' ? { evidenceAppendix: detailedInterpretations.flatMap((result) => result.claims.map((claim) => ({ topic: result.topic, claimKey: claim.claimKey, ruleIds: claim.ruleIds, sourceLocators: claim.sourceLocators, evidenceRefs: claim.evidenceRefs }))) } : {}),
    disclosure: '列印內容為傳統紫微斗數規則式解讀與自我探索資訊；判讀強度不是科學準確率，也不應單獨用於醫療、法律或重大財務決策。',
  };
}
